import { FC, useContext } from 'react'

import { AuthSessionContext } from '@context/AuthContext'
import { useProductReadContext } from '@context/ProductContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2 } from '@ui/Typography'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { formatDate, withTimeFormat } from '@utils/date.utils'
import { TransactionContext, getTransactionDisplay } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { AnimatedListItem } from '@shared/animations/AnimatedListItem'
import { Box } from '@shared/components/Box'
import { TransactionTypeWithStatusIcon } from '@shared/components/TransactionTypeIcon/TransactionTypeWithStatusIcon'

import styles from './transactionHistoryTable.module.scss'

interface Props {
  tx: TransactionDto
  context: TransactionContext
  walletId?: string
  id?: string
  onClick: (tx: TransactionDto) => void
}

export const TransactionHistoryMobileRow: FC<Props> = ({ tx, context, walletId, id, onClick }) => {
  const [auth] = useContext(AuthSessionContext)
  const isArabic = useArabicSessionLanguage()
  const { isDefaultCFDProductType } = useProductReadContext()
  const { formatMoney } = useFormatNumber()
  const dateFormat = auth?.dateFormatType?.name

  const { fields, amountSign } = getTransactionDisplay(tx, context, {
    isArabic,
    authId: auth?.id,
    isDefaultCFDProductType: isDefaultCFDProductType(),
    walletId,
  })

  const formattedAmount = `${amountSign}${formatMoney(
    fields.amount,
    fields.currency as CurrencyType
  )}`
  const formattedDate = formatDate(tx.createdDate, { formatType: withTimeFormat(dateFormat) })

  return (
    <AnimatedListItem id={id} className={styles.mobileRow} onClick={() => onClick(tx)}>
      <TransactionTypeWithStatusIcon transaction={tx} status={tx.state.id} />
      <Box flex direction='column' gap='x2' flexItem='1' minWidth={0}>
        <Body2 medium>{tx.type.name}</Body2>
        <SensitiveData>
          <Body2 color='text-low-em'>{formattedDate}</Body2>
        </SensitiveData>
      </Box>
      <SensitiveData>
        <Body2 medium color='text-med-em' whiteSpace='nowrap'>
          {formattedAmount}
        </Body2>
      </SensitiveData>
      <ChevronIcon.Right size={32} color='text-low-em' />
    </AnimatedListItem>
  )
}
